import React from 'react';

/**
 * One pill that FLOWS to whichever element is currently active.
 *
 * Shared by the tab strips and the sidebar so both move with the same curve —
 * two copies of this would drift apart the first time one of them was tuned.
 *
 * Why it reads as liquid rather than as a sliding box
 * ---------------------------------------------------
 * A rigid rectangle moved from A to B reads as a rectangle being moved. Liquid
 * reads as liquid because its leading edge arrives before its trailing edge:
 * it stretches toward where it is going, then the back catches up and it
 * settles. That is squash-and-stretch, and it is the whole effect.
 *
 * So the travel runs through THREE keyframes rather than two: the old box, a
 * middle frame spanning the union of old and new — the instant the pill is
 * touching both ends at once — and the new box. The Web Animations API is used
 * instead of a CSS transition because a transition can only interpolate
 * between two states and cannot express that middle stretch.
 *
 * Position is read from the DOM rather than passed in as a value. This
 * component does not know what "active" means to its container, and reading
 * the rendered result means it tracks every way the selection can change —
 * click, keyboard, a parent setting state, or the router restoring a route.
 *
 * Only `transform`, `width` and `height` move, so the travel composites and
 * never triggers layout on the list it sits in.
 */
export default function SlidingIndicator({
  containerRef,            // optional: defaults to the pill's own parent
  activeSelector,
  axis = 'x',              // 'x' for a tab strip, 'y' for a vertical nav
  className = '',
  // ~820ms: slow enough to read as thick liquid, short enough that the
  // strip never feels unresponsive. The label colour flips much sooner
  // (see TabsTrigger), so the tab ACKNOWLEDGES the click immediately
  // while the pill is still travelling.
  duration = 820,
  deps = [],
}) {
  const pill = React.useRef(null);
  // Hidden until first measured, or it flashes at the container's origin
  // before settling into place on mount.
  const [ready, setReady] = React.useState(false);

  React.useLayoutEffect(() => {
    // The pill is rendered INSIDE the container, so its own parent is the
    // container — and a node's own ref is attached before its own layout
    // effect runs. Reading a parent's ref here would be too early: React
    // attaches child refs before parent refs, so `containerRef.current` is
    // still null on the first pass and the pill never gets measured.
    const box = containerRef?.current || pill.current?.parentElement;
    if (!box) return undefined;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Where the pill is right now, so the next move can start from it.
    let at = null;
    // First placement is a JUMP, every later one flows.
    //
    // Each page renders its own <Sidebar/>, so navigating remounts it. Without
    // this the pill would flow in from the top of the nav on every route
    // change — reading as "moved from Dashboard" no matter where you actually
    // came from. A tab switch does not remount, so tabs still flow.
    let first = true;

    const place = () => {
      const active = box.querySelector(activeSelector);
      const bar = pill.current;
      if (!bar) return;
      if (!active) {
        setReady(false);
        box.removeAttribute('data-indicator-ready');   // let the fallback paint
        return;
      }

      // Measured against the container's own box so nesting depth does not
      // matter — a nav item inside two wrappers lands as accurately as a tab
      // that is a direct child. Scroll offset is added back because the pill
      // is positioned inside the scrolled content.
      const a = active.getBoundingClientRect();
      const c = box.getBoundingClientRect();
      const to = {
        x: Math.round(a.left - c.left + box.scrollLeft),
        y: Math.round(a.top - c.top + box.scrollTop),
        w: Math.round(a.width),
        h: Math.round(a.height),
      };

      const frame = (g) => ({
        // Both offsets ride in ONE transform: assigning `transform` replaces
        // any translate a utility class applied, so centring must live in it.
        transform: `translate(${g.x}px, ${g.y}px)`,
        width: `${g.w}px`,
        height: `${g.h}px`,
      });

      const settle = () => {
        Object.assign(bar.style, frame(to));
        at = to;
        setReady(true);
        // Tells the container the pill is doing its job, which switches OFF
        // the CSS fallback. Without it the active item would be white text
        // with nothing behind it the moment anything stops the pill painting.
        box.setAttribute('data-indicator-ready', '');
      };

      const moved = at && (at.x !== to.x || at.y !== to.y || at.w !== to.w || at.h !== to.h);
      if (first || !at || !moved || still.matches || !bar.animate) {
        first = false;
        settle();
        return;
      }

      // The stretch: a box containing BOTH the old and the new position. At
      // this instant the pill spans the whole journey, which is what makes the
      // leading edge look like it arrived first.
      const left = Math.min(at.x, to.x);
      const top = Math.min(at.y, to.y);
      const span = {
        x: left,
        y: top,
        w: Math.max(at.x + at.w, to.x + to.w) - left,
        h: Math.max(at.y + at.h, to.y + to.h) - top,
      };

      const from = at;
      // The element is given its FINAL value first; the animation only replays
      // the journey on top of it. So when the animation ends — or is dropped
      // by the browser — the pill is already where it belongs and nothing
      // snaps back.
      settle();
      bar.animate(
        [
          frame(from),
          // The stretch peaks just past the midpoint — after the leading
          // edge has committed, which is when a liquid actually looks
          // stretched. Its own gentle curve keeps the spread from snapping
          // open.
          { ...frame(span), offset: 0.46, easing: 'cubic-bezier(0.42,0,0.3,1)' },
          frame(to),
        ],
        {
          duration,
          // Eased at BOTH ends. The old easeOutQuint left the mark almost at
          // full speed, which reads as a flick; thick oil builds up and lets
          // go slowly. No control point above 1, so it settles without any
          // overshoot or bounce.
          easing: 'cubic-bezier(0.37,0,0.18,1)',
        },
      );
    };

    place();
    // Webfonts land after first paint and change every label's width.
    if (document.fonts?.ready) document.fonts.ready.then(place).catch(() => {});

    // Attributes cover the state flip; childList covers items appearing or
    // disappearing as permissions and groups change.
    const mo = new MutationObserver(place);
    mo.observe(box, { attributes: true, childList: true, subtree: true,
                      attributeFilter: ['data-state', 'aria-current', 'class'] });

    const ro = new ResizeObserver(place);
    ro.observe(box);
    Array.from(box.children).forEach((c) => ro.observe(c));

    // A collapsing group moves the items below it without resizing the
    // container or touching any attribute, so follow the transition itself.
    box.addEventListener('transitionend', place);
    box.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    return () => {
      mo.disconnect(); ro.disconnect();
      box.removeEventListener('transitionend', place);
      box.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, activeSelector, duration, ...deps]);

  return (
    <span
      ref={pill}
      aria-hidden="true"
      data-sliding-indicator={axis}
      className={`pointer-events-none absolute left-0 top-0 ${className}`}
      // No `transition` here: the Web Animations API drives the travel, and a
      // CSS transition would fight it for the same properties.
      style={{ opacity: ready ? 1 : 0, willChange: 'transform, width, height' }} />
  );
}
